import { useState, useEffect, useCallback } from 'react'
import { supabase, safeQuery } from '../lib/supabase'
import { getCached, setCached } from '../lib/queryCache'
import { getEntity, setEntity, seedEntityList } from '../lib/entityCache'
import { useAutoRefresh } from './useAutoRefresh'
import { localFallback, localFirstRows, localUpsert, localDelete } from '../lib/syncEngine'
import { getLocalDb, getSyncState } from '../lib/localDb'
import { invalidate } from '../lib/dataInvalidation'
import { queuedWrite } from '../lib/queuedWrite'
import { getSessionWithTimeout } from '../lib/authSession'

const PAGE_SIZE = 100

// ─── Name-aware search parser ───────────────────────────────────────────────
// Before space: search first_name OR last_name with full input
// After space: first word → first_name, remaining (skip middle initials) → last_name
// Middle initial = single char or char + "." (e.g. "M", "M.")

function parseNameSearch(input) {
  const trimmed = (input || '').trim()
  if (!trimmed) return { mode: 'none' }

  const parts = trimmed.split(/\s+/).filter(Boolean)
  if (parts.length === 1) {
    return { mode: 'single', term: parts[0] }
  }

  // Multi-word: first part is first_name, skip middle initials only if
  // there's a longer word after them, rest is last_name
  const firstName = parts[0]
  const afterFirst = parts.slice(1)

  // Only skip a part as a middle initial if it's short AND not the last part
  const rest = afterFirst.filter((p, i) => {
    const isShort = p.length === 1 || (p.length === 2 && p.endsWith('.'))
    const isLast = i === afterFirst.length - 1
    // Keep it if it's the last part (user is typing last name) or if it's not short
    return !isShort || isLast
  })
  const lastName = rest.join(' ') || ''

  return { mode: 'full', firstName, lastName }
}

// Build Supabase filter and client-side matcher from parsed name search
function buildNameFilter(parsed) {
  if (parsed.mode === 'none') return { dbFilter: null, clientFilter: null }
  if (parsed.mode === 'single') {
    return {
      // search_names = first + last + aliases, so a contact who changed their
      // name is still findable under the one on older emails.
      dbFilter: `first_name.ilike.%${parsed.term}%,last_name.ilike.%${parsed.term}%,search_names.ilike.%${parsed.term}%`,
      clientFilter: null, // DB filter is sufficient for single word
    }
  }
  // Full name mode: query by first_name for DB, narrow by last_name client-side
  return {
    dbFilter: `first_name.ilike.%${parsed.firstName}%`,
    clientFilter: (c) => {
      const first = (c.first_name || '').toLowerCase()
      const last = (c.last_name || '').toLowerCase()
      const matchFirst = first.includes(parsed.firstName.toLowerCase())
      const matchLast = parsed.lastName
        ? last.includes(parsed.lastName.toLowerCase())
        : true
      return matchFirst && matchLast
    },
  }
}

export function useContacts(filters = {}) {
  const [page, setPage] = useState(0)
  const cacheKey = `contacts:${JSON.stringify(filters)}:${page}`
  const cached = getCached(cacheKey)
  const [contacts, setContacts] = useState(cached || [])
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState(null)
  const [total, setTotal] = useState(0)

  const fetchContacts = useCallback(async (pageNum) => {
    const p = pageNum ?? page
    console.log('Fetching contacts page', p)
    if (!getCached(cacheKey)) setLoading(true)
    setError(null)
    // Local-first paint (desktop shell only): instant first page from the
    // on-disk mirror while the network fetch runs; a network SUCCESS replaces
    // them wholesale (networkOk guard — a fast network failure must not
    // suppress the local paint). Mirror rows lack the batch-joined
    // organization object — same shape the fallback path serves.
    let networkOk = false
    const unfiltered = !filters.search && !filters.entity_id && !filters.organization_id
      && !filters.priority_tier && !filters.contact_type
    if (p === 0 && unfiltered && !getCached(cacheKey)) {
      localFirstRows('contacts', { orderBy: 'last_name ASC NULLS LAST', limit: PAGE_SIZE }).then((local) => {
        if (!local || networkOk || getCached(cacheKey)?.length) return
        setContacts(local); setTotal(local.length); setLoading(false)
      }).catch(() => {})
    }
    try {
      const searchTerm = (filters.search || '').trim()
      const isNameSearch = !!searchTerm

      let results = []
      let count = 0

      if (isNameSearch) {
        // ── Accent-insensitive name search via RPC ──────────────────────────
        // Uses Postgres unaccent() so "Alvaro" matches "Álvaro", "Garcia" → "García" etc.
        // Falls back to ilike if the RPC isn't deployed yet.
        let rpcOk = false
        try {
          const { data: rpcIds, error: rpcErr } = await supabase
            .rpc('search_contacts_unaccent_ids', { p_query: searchTerm, p_limit: 500 })
          if (!rpcErr && rpcIds && rpcIds.length >= 0) {
            rpcOk = true
            const ids = (rpcIds || []).map(r => r.id).filter(Boolean)
            if (ids.length === 0) {
              results = []
            } else {
              let q = supabase
                .from('contacts')
                .select('id, first_name, last_name, title, organization_id, emails, phones, priority_tier, relationship_strength, is_client, contact_type, disciplines, last_contact_date, entity_id, reports_to, department, created_at')
                .in('id', ids)
                .order('last_name', { ascending: true, nullsFirst: false })
              if (filters.entity_id) q = q.eq('entity_id', filters.entity_id)
              if (filters.organization_id) q = q.eq('organization_id', filters.organization_id)
              if (filters.priority_tier) q = q.eq('priority_tier', filters.priority_tier)
              const { data } = await safeQuery(q)
              results = data || []
            }
          }
        } catch { /* RPC not yet deployed — fall through to ilike */ }

        if (!rpcOk) {
          // Fallback: original ilike approach (accent-sensitive but always works)
          const parsed = parseNameSearch(searchTerm)
          const { dbFilter, clientFilter } = buildNameFilter(parsed)
          if (dbFilter) {
            let q = supabase
              .from('contacts')
              .select('id, first_name, last_name, title, organization_id, emails, phones, priority_tier, relationship_strength, is_client, contact_type, disciplines, last_contact_date, entity_id, reports_to, department, created_at')
              .or(dbFilter)
              .order('last_name', { ascending: true, nullsFirst: false })
              .limit(200)
            if (filters.entity_id) q = q.eq('entity_id', filters.entity_id)
            if (filters.organization_id) q = q.eq('organization_id', filters.organization_id)
            if (filters.priority_tier) q = q.eq('priority_tier', filters.priority_tier)
            const { data } = await safeQuery(q)
            results = data || []
            if (clientFilter) results = results.filter(clientFilter)
          }
        }

        // Client-side contact_type filter
        if (filters.contact_type) {
          results = results.filter(c =>
            Array.isArray(c.contact_type) && c.contact_type.includes(filters.contact_type)
          )
        }
        count = results.length

      } else {
        // ── Normal paginated list (no search term) ─────────────────────────
        let query = supabase
          .from('contacts')
          .select('id, first_name, last_name, title, organization_id, emails, phones, priority_tier, relationship_strength, is_client, contact_type, disciplines, last_contact_date, entity_id, reports_to, department, created_at', { count: 'estimated' })
          .order('last_name', { ascending: true, nullsFirst: false })
          .range(p * PAGE_SIZE, (p + 1) * PAGE_SIZE - 1)

        if (filters.entity_id) query = query.eq('entity_id', filters.entity_id)
        if (filters.organization_id) query = query.eq('organization_id', filters.organization_id)
        if (filters.priority_tier) query = query.eq('priority_tier', filters.priority_tier)

        let { data, error: err, count: cnt } = await safeQuery(query)
        if (err && (err.message?.includes('timeout') || err.message?.includes('57014'))) {
          console.warn('Contacts query timeout — retrying...')
          const retry = await safeQuery(query)
          data = retry.data; err = retry.error; cnt = retry.count
        }
        if (err) {
          setError(err.message)
          if (!getCached(cacheKey)) {
            const local = await localFallback('contacts', { orderBy: 'last_name ASC NULLS LAST' })
            if (local.length) { setContacts(local); setTotal(local.length) }
          }
          return
        }
        results = data || []
        count = cnt || 0

        if (filters.contact_type) {
          results = results.filter(c =>
            Array.isArray(c.contact_type) && c.contact_type.includes(filters.contact_type)
          )
          count = results.length
        }
      }

      networkOk = true // fresh rows win; suppress any late local-first paint
      // Batch-fetch company names for rows that have organization_id.
      const orgIds = [...new Set(results.map(r => r.organization_id).filter(Boolean))]
      if (orgIds.length > 0) {
        try {
          const { data: companies } = await safeQuery(
            supabase.from('companies').select('id, name').in('id', orgIds)
          )
          const companyMap = new Map((companies || []).map(c => [c.id, c]))
          results = results.map(r => ({
            ...r,
            organization: r.organization_id ? companyMap.get(r.organization_id) || null : null,
          }))
        } catch { /* non-fatal */ }
      }

      setContacts(results)
      setTotal(count)
      setCached(cacheKey, results)
      seedEntityList('contacts', results)
    } catch (err) {
      setError(err.message)
      if (!getCached(cacheKey)) {
        const local = await localFallback('contacts', { orderBy: 'last_name ASC NULLS LAST' })
        if (local.length) { setContacts(local); setTotal(local.length) }
      }
    } finally {
      setLoading(false)
    }
  }, [JSON.stringify(filters), page, cacheKey])

  useEffect(() => { fetchContacts() }, [fetchContacts])

  const goToPage = useCallback((p) => {
    setPage(p)
  }, [])

  const silentRefetch = useCallback(() => fetchContacts(), [fetchContacts])
  useAutoRefresh(silentRefetch, { intervalMs: 120_000 })

  return { contacts, loading, error, total, page, pageSize: PAGE_SIZE, goToPage, refetch: fetchContacts }
}

export function useContact(id) {
  const cached = id ? getEntity('contacts', id) : null
  const [contact, setContact] = useState(cached || null)
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState(null)

  const fetchContact = useCallback(async () => {
    if (!id) return
    console.log('Fetching contact...')
    if (!getEntity('contacts', id)) setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await safeQuery(
        supabase
          .from('contacts')
          .select('*')
          .eq('id', id)
          .single()
      )
      if (err) { setError(err.message); return }
      setContact(data)
      if (data) setEntity('contacts', id, data)
    } catch (err) {
      setError(err.message)
      setContact(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { fetchContact() }, [fetchContact])

  return { contact, loading, error, refetch: fetchContact }
}

export async function createContact(data) {
  const result = await queuedWrite('contacts', 'insert', data)
  localUpsert('contacts', result).catch(() => {})
  invalidate('contacts', { id: result?.id, op: 'create' })
  return result
}

export async function updateContact(id, data) {
  const result = await queuedWrite('contacts', 'update', data, id)
  localUpsert('contacts', result).catch(() => {})
  invalidate('contacts', { id, op: 'update' })
  return result
}

export async function deleteContact(id) {
  await queuedWrite('contacts', 'delete', null, id)
  localDelete('contacts', id).catch(() => {})
  invalidate('contacts', { id, op: 'delete' })
}

// Bound any promise so a wedged local DB or hung query can never stall the
// caller. On timeout the value rejects and the caller falls through.
function _withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timeout`)), ms)),
  ])
}

export async function searchContacts(query) {
  const q = (query || '').trim()
  if (!q) return []

  // ── Local PGlite search (instant, no network) ─────────────────────────────
  // PGlite is synced from Supabase at startup. first_name and last_name are
  // indexed top-level columns; _data JSONB holds the full row including emails.
  // Only use local if contacts have been synced at least once — otherwise the
  // table is empty and we'd silently return no results.
  // Every local await is timeout-bounded (3s): a wedged WASM worker must fall
  // through to Supabase rather than hang the recipient field.
  try {
    const db = getLocalDb()
    if (db) {
      const syncState = await _withTimeout(getSyncState('contacts'), 3000, 'getSyncState')
      if (syncState?.last_synced_at) {
        const parsed = parseNameSearch(q)
        let rows = []
        if (parsed.mode === 'single') {
          ;({ rows } = await _withTimeout(db.query(
            `SELECT _data FROM contacts
             WHERE first_name ILIKE $1 OR last_name ILIKE $1
             ORDER BY last_name ASC NULLS LAST
             LIMIT 20`,
            [`%${q}%`]
          ), 3000, 'pglite contacts'))
        } else if (parsed.mode === 'full') {
          ;({ rows } = await _withTimeout(db.query(
            `SELECT _data FROM contacts
             WHERE first_name ILIKE $1 AND last_name ILIKE $2
             ORDER BY last_name ASC NULLS LAST
             LIMIT 20`,
            [`%${parsed.firstName}%`, `%${parsed.lastName}%`]
          ), 3000, 'pglite contacts'))
        }
        const seen = new Set()
        const local = rows
          .map(r => r._data)
          .filter(c => {
            if (!c?.id || seen.has(c.id)) return false
            seen.add(c.id)
            return true
          })
        // Only short-circuit when the local mirror actually had a match. If it
        // returns nothing, the contact may exist in Supabase but not yet in the
        // PGlite copy (incomplete/stale sync) — fall through to the network
        // query rather than reporting "no contact found" for someone real.
        if (local.length > 0) return local
      }
    }
  } catch (err) {
    console.warn('[searchContacts] local query failed, falling back to Supabase:', err?.message)
  }

  // ── Supabase fallback (PGlite unsynced, or local returned no match) ────────
  try {
    try {
      const { data: rpcData, error: rpcErr } = await supabase
        .rpc('search_contacts_unaccent', { p_query: q, p_limit: 20 })
      if (!rpcErr && Array.isArray(rpcData)) {
        // This RPC is SECURITY DEFINER (bypasses RLS), so drop private contacts
        // not owned by the current user before they reach a picker dropdown.
        const { data: { session } } = await getSessionWithTimeout()
        const uid = session?.user?.id || null
        const seen = new Set()
        return rpcData.filter(c => {
          if (c.is_private && c.owner_user_id !== uid) return false
          if (seen.has(c.id)) return false; seen.add(c.id); return true
        })
      }
    } catch { /* RPC not deployed yet — fall through */ }

    const parsed = parseNameSearch(q)
    const { dbFilter, clientFilter } = buildNameFilter(parsed)
    if (!dbFilter) return []

    const { data, error } = await supabase
      .from('contacts')
      .select('*')
      .or(dbFilter)
      .order('last_name', { ascending: true })
      .limit(20)
    if (error) throw error

    const seen = new Set()
    return (data || []).filter(c => {
      if (seen.has(c.id)) return false
      seen.add(c.id)
      return clientFilter ? clientFilter(c) : true
    })
  } catch (err) {
    console.error('searchContacts error:', err)
    return []
  }
}
